import { CellValueChangedEvent, ColDef, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, Optional, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { InvoiceHeader, InvoiceStatus, JoHeader, ShipBin, ShipFrequency, Shipment, ShipmentStatusEnum } from "app/model/entities/entity-model";
import { InvoiceStatusCode } from "app/model/enums/invoice-status-code";

import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { MenuAction } from "app/shared/menu-renderer.component";
import { BarcodeService } from "app/shared/barcode.service";
import { DateFns } from "app/shared/date-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent, wrapBusy } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { NavFns } from "app/shared/nav-fns";
import { UtilFns } from "app/shared/util-fns";
import { environment } from "environments/environment";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";

import { InvoiceShipmentService } from "./invoice-shipment.service";
import { PackingSlipPrintDialogComponent } from "./packing-slip-print-dialog.component";
import { ProcessManagerComponent } from "./process-manager.component";
import { AddressValidationService } from "@/shared/address-validation.service";

import { InvoiceDialogComponent } from "@/shared/invoice-dialog.component";
import { FreightExplanationDialogComponent } from "./freight-explanation-dialog.component";

@Component({
  selector: "app-jo-ship",
  templateUrl: "./jo-ship.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class JoShipComponent extends DomainBaseComponent {
  shipBinGridOptions!: GridOptionsExt;
  shipBins!: ShipBin[];
  allOpenShipBins!: ShipBin[];
  selectedShipBin!: ShipBin | null;
  showCurrentBinsOnly = false;

  invoiceHeaders: InvoiceHeader[] = [];
  invhGridOptions!: GridOptionsExt;

  currentShipment!: Shipment | null;
  pdfurl!: string;

  /** Re-entry guard for the pickup actions. Flipped before their confirm dialogs open. */
  private isPickupActionBusy = false;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected invoiceShipmentService: InvoiceShipmentService,
    protected barcodeService: BarcodeService,
    private addressValidationService: AddressValidationService,
    @Optional() private processManager: ProcessManagerComponent,
  ) {
    super(domainService);

    this.dbQueryService.getAllCached(InvoiceStatus);
    this.dbQueryService.getAllCached(ShipFrequency);

    if (this.processManager) {
      setTimeout(() => {
        this.processManager.showShipFrequenciesButton = true;
      });
    }

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  override ngOnDestroy() {
    if (this.processManager) {
      this.processManager.showShipFrequenciesButton = false;
    }
    super.ngOnDestroy();
  }

  async updateFromContext() {
    this.shipBinGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onShipBinGridReady,
      onRowSelected: this.onShipBinRowSelected,
      onFirstDataRendered: (params) => {
        this.applyLocation();
        this.AgFns.sizeColumnsToFit(this.shipBinGridOptions);
      },
    });

    this.captureLocation(this.shipBinGridOptions, "id");

    this.invhGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onInvGridReady,
      onRowSelected: this.onInvRowSelected,
      onCellValueChanged: this.onCellValueChanged,
      onFirstDataRendered: (params) => this.AgFns.sizeColumnsToFit(this.invhGridOptions),
    });

    const vInvoiceHeaders = await this.dbQueryService.getVoucherInvoices();

    this.allOpenShipBins = _.uniq(vInvoiceHeaders.map((invh) => invh.shipBin));
    this.allOpenShipBins = this.allOpenShipBins.filter((x) => x != null);
    this.refreshAndUpdate();

    this.isPageReady = true;
  }

  refreshAndUpdate() {
    this.replaceLocation(this.selectedShipBin?.id);
    const todayMs = DateFns.startOfDay(new Date()).getTime();
    if (this.showCurrentBinsOnly) {
      this.shipBins = this.allOpenShipBins.filter((x) => x.expectedShipDate.getTime() <= todayMs);
    } else {
      this.shipBins = this.allOpenShipBins;
    }
    if (!this.shipBins.includes(<ShipBin>this.selectedShipBin)) {
      this.selectedShipBin = null;
      this.invoiceHeaders = [];
      this.currentShipment = null;
    }

  }

  onShipBinGridReady(event: GridReadyEvent) {
    this.shipBinGridOptions.rowSelection = "single";
    const colDefs = [
      {
        ...AgFns.createButtonProps("", this.printShipBinLabel.bind(this), { label: "Print Ship Bin Label" }),
        minWidth: 150,
      },
      { headerName: "Ship Bin Id", field: "id", ...AgFns.fixWidth(140) },
      { headerName: "Frequency", field: "firstJoh.shipFrequency.name", ...AgFns.fixWidth(140) },
      { headerName: "Exp, Ship Date", field: "expectedShipDate", ...AgFns.fixWidth(160) },
      { headerName: "Account", field: "firstJoh.account.accountName", ...AgFns.flexWidth(100) },
      {
        headerName: "GL Account Ref",
        field: "firstJoh.account.gLAccountRef",
        valueFormatter: (params: any) => params.value || "— missing —",
        cellStyle: (params: any) => !params.value ? { color: "var(--p-red-500, #ef4444)", fontWeight: 600 } : null,
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "Ship To",
        valueGetter: (params: any) => {
          const joh = params.data?.firstJoh;
          if (!joh) return "";
          const parts = [joh.shippingName, joh.shippingAddress1, joh.shippingAddress2, [joh.shippingCity, joh.shippingState, joh.shippingZipCode].filter(Boolean).join(" ")].filter(Boolean);
          return parts.join(", ");
        },
        ...AgFns.flexWidth(300),
      },
      {
        ...AgFns.createCheckboxProps("Manifest?", ""),
        editable: false,
        valueGetter: (params: any) => params.data?.firstJoh?.manifestId != null,
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Inv. Count",
        field: "invoiceHeaders.length",
        type: "numericColumn",
        ...AgFns.fixWidth(140)
      },
    ];
    const sortFields = ["firstJoh.account.accountName"];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: "asc" as const };
    });

    // Grey out and block selection for bins whose account has no QBO GL Account Ref.
    // Shipping is blocked at the invoice-post level anyway — this surfaces it up-front.
    this.shipBinGridOptions.getRowStyle = (params: any) => {
      const hasRef = !!params.data?.firstJoh?.account?.gLAccountRef;
      return hasRef ? null : { opacity: 0.5, pointerEvents: "none", background: "var(--p-surface-100, #f1f5f9)" };
    };
    this.shipBinGridOptions.isRowSelectable = (rowNode: any) => {
      return !!rowNode.data?.firstJoh?.account?.gLAccountRef;
    };

    AgFns.initGrid(event, colDefs, sortModel);
    this.AgFns.sizeColumnsToFit(this.invhGridOptions);
  }

  onInvGridReady(event: GridReadyEvent) {
    this.invhGridOptions.rowSelection = "single";
    const colDefs: ColDef[] = [
      {
        colId: "actions",
        ...AgFns.createMenuProps("", this.getInvoiceMenuActions(), { buttonLabel: "Actions" }),
        ...AgFns.fixWidth(130),
      },
      { headerName: "Shipment", field: "shipmentId", ...AgFns.flexWidth(160) },
      {
        ...AgFns.createButtonProps("Invoice", this.openInvoiceDialog.bind(this), {
          calcLabel: (invh: InvoiceHeader) => invh.id,
        }),
        field: "id",
        ...AgFns.flexWidth(180),
      },
      { headerName: "Status", field: "invoiceStatus.name", ...AgFns.flexWidth(160) },
      {
        ...AgFns.createCheckboxProps("Acct Pays Freight", "joHeader.doesAccountPayFreight"),
        headerTooltip: "When checked, the account is charged freight on invoices.",
        ...AgFns.flexWidth(180),
      },
      {
        ...AgFns.createCheckboxProps("1st Ship Only", "joHeader.shouldChargeFreightOnFirstShipmentOnly"),
        headerTooltip: "When checked, freight is only charged on first shipment invoices. Backorders ship freight-free.",
        ...AgFns.flexWidth(160),
      },
      {
        ...AgFns.createCheckboxProps("Primary Invoice", "isPrimaryInvoice"),
        editable: true,
        ...AgFns.flexWidth(170),
      },
      // { headerName: "Date", field: "invoiceDate", ...AgFns.fixWidth("date") },
      // { headerName: "Status", field: "invoiceStatus.name", ...AgFns.fixWidth(100) },
      {
        headerName: "Base Freight",
        type: "numericColumn",
        valueFormatter: (params) => UtilFns.fmtCurrency(params.value),
        valueGetter: (params) => {
          const invh = <InvoiceHeader>params.data;
          return invh?.isPrimaryInvoice ? invh.shipment?.freightAmt : null;
        },
        ...AgFns.flexWidth(170),
      },
      {
        ...AgFns.createButtonProps("Actual Charged", this.openFreightExplanation.bind(this), {
          calcLabel: (invh: InvoiceHeader) => {
            if (!invh?.shipment?.freightAmt) return "";
            if (invh.isPrimaryInvoice) {
              return UtilFns.fmtCurrency(invh.calcShipFreightAmt());
            }
            return "View";
          },
          canDisplay: (invh: InvoiceHeader) => !!invh?.shipment?.freightAmt,
          icon: "pi pi-info-circle",
        }),
        headerTooltip: "The freight amount that will actually appear on this invoice. Click to see how it was calculated.",
        ...AgFns.flexWidth(200),
      },
      // { ...AgFns.createCellButtonProps('Shipping Label', this.shipLabelCell) },
      {
        headerName: "Label",
        colId: "shipmentLabel",
        valueGetter: (params: any) => {
          const invh = <InvoiceHeader>params.data;
          return invh?.isPickup ? "Customer Pickup" : (invh?.shipment?.labelApiIdentifier ?? null);
        },
        ...AgFns.flexWidth(140),
      },
    ];

    /*     if (this.user.isAccountingAdmin) {
      var colDef = { ...AgFns.createButtonProps('Print Invoice', this.printInvoice.bind(this),
        { label: 'Print Invoice',  canDisplay: this.canPrintInvoice.bind(this) }),
       minWidth: 170
      };
      colDefs.push(colDef);
    } */

    AgFns.initGrid(event, colDefs, null);
    AgFns.autoSizeAllColumns(this.invhGridOptions);
  }

  /** Returns the menu actions for the invoice grid Actions dropdown */
  getInvoiceMenuActions(): MenuAction[] {
    return [
      {
        label: "Print Ship Label",
        icon: "pi pi-print",
        action: (invh: InvoiceHeader) => this.printInvoicePackageLabel(invh),
        canDisplay: (invh: InvoiceHeader) => this.invoiceHasLabel(invh),
      },
      {
        label: "Print Packing Slip",
        icon: "pi pi-file",
        action: (invh: InvoiceHeader) => this.printPackingSlip(invh),
      },
      { label: "", action: () => {}, separator: true },
      {
        label: "Add to Shipment",
        calcLabel: (invh: InvoiceHeader) => <string>this.calcAddToRemoveFromShipmentLabel(invh),
        icon: "pi pi-plus-circle",
        action: (invh: InvoiceHeader) => this.addToRemoveFromShipment(invh),
        canDisplay: (invh: InvoiceHeader) => this.canDisplayAddToRemoveFromShipment(invh),
      },
      {
        label: "Calculate Freight",
        icon: "pi pi-calculator",
        action: (invh: InvoiceHeader) => this.calcFreight(invh),
        canDisplay: (invh: InvoiceHeader) => this.canCalcFreight(invh),
      },
      {
        label: "Print Shipping Label",
        icon: "pi pi-tag",
        action: (invh: InvoiceHeader) => this.printShippingLabel(invh),
        canDisplay: (invh: InvoiceHeader) => this.canPrintShippingLabel(invh),
      },
      {
        label: "Release for Pickup",
        icon: "pi pi-box",
        action: (invh: InvoiceHeader) => this.releaseForPickup(invh),
        canDisplay: (invh: InvoiceHeader) => this.canReleaseForPickup(invh),
      },
      {
        label: "Undo Pickup Release",
        icon: "pi pi-replay",
        action: (invh: InvoiceHeader) => this.undoPickupRelease(invh),
        canDisplay: (invh: InvoiceHeader) => this.canUndoPickupRelease(invh),
      },
      {
        label: "Void Label",
        calcLabel: (invh: InvoiceHeader) => <string>this.calcVoidActionLabel(invh),
        icon: "pi pi-times-circle",
        action: (invh: InvoiceHeader) => this.voidAction(invh),
        canDisplay: (invh: InvoiceHeader) => this.canVoidAction(invh),
      },
    ];
  }

  get title() {
    return `Current Shipment: ${this.currentShipment?.id ?? "New"} `;
  }

  onShipBinRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    this.selectedShipBin = e.data as ShipBin;
    this.replaceLocation(this.selectedShipBin.id);

    // Block: account has no QBO GL Account Ref — nothing can ship.
    if (!this.selectedShipBin?.firstJoh?.account?.gLAccountRef) {
      this.invoiceHeaders = [];
      this.currentShipment = null;
      return;
    }

    this.invoiceHeaders = this.selectedShipBin.invoiceHeaders;
    this.showHideVoucherCols();

    this.currentShipment = null;

  }

  showHideVoucherCols() {
    const todayMs = DateFns.startOfDay(new Date()).getTime();
    //const canShow = this.selectedShipBin.expectedShipDate.getTime() <= todayMs;
    this.invhGridOptions?.apiX?.setColumnsVisible(["isPrimaryInvoice", "shipmentId"], this.canShip());
  }

  onInvRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    const invoiceHeader = e.data as InvoiceHeader;
    if (!invoiceHeader) {
      return;
    }

    // Cannot expand or remove from a shipment that is labeled or released for pickup
    if (invoiceHeader.shipment && this.isShipmentOpen(invoiceHeader.shipment)) {
      this.currentShipment = invoiceHeader.shipment;
    }

  }

  async onCellValueChanged(e: CellValueChangedEvent) {
    if (e.colDef.field === "isPrimaryInvoice") {
      this.invhGridOptions.apiX?.redrawRows();
      await this.dbSaveService.saveChanges();
      this.domainService.toastService.success("Primary invoice updated.");
    }
  }

  canShip() {
    const todayMs = DateFns.startOfDay(new Date()).getTime();
    if (this.selectedShipBin == null) {
      return false;
    }
    const canShip = this.selectedShipBin.expectedShipDate.getTime() <= todayMs;
    return canShip;
  }

  canAddAllToCurrentShipment() {
    return this.invoiceHeaders.some((invh) => this.canAddToShipment(invh));
  }

  async addAllToCurrentShipment() {
    await this.busyInvGrid(async () => {
      const invhs = this.invoiceHeaders.filter((invh) => invh.shipmentId == null && this.isShipmentOpen(this.currentShipment));
      if (invhs.length == 0) {
        return;
      }
      if (this.currentShipment == null) {
        this.currentShipment = this.uow.createEntity(Shipment, {
          modTs: new Date(),
        });
      }
      invhs.forEach((invh) => this.addToShipmentCore(invh));
      await this.dbSaveService.saveChanges();
      this.hackInvGridRefreshRows();
  
    });
  }

  async addToRemoveFromShipment(invh: InvoiceHeader) {
    if (this.canAddToShipment(invh)) {
      this.addToShipment(invh);
    } else if (this.canRemoveFromShipment(invh)) {
      this.removeFromShipment(invh);
    }
  }

  calcAddToRemoveFromShipmentLabel(invh: InvoiceHeader) {
    if (this.canAddToShipment(invh)) {
      return "Add to shipment";
    } else if (this.canRemoveFromShipment(invh)) {
      return "Remove from shipment";
    }
  }

  canDisplayAddToRemoveFromShipment(invh: InvoiceHeader) {
    if (this.canAddToShipment(invh)) {
      return true;
    } else if (this.canRemoveFromShipment(invh)) {
      return true;
    } else {
      return false;
    }
  }

  canAddToShipment(invoiceHeader: InvoiceHeader) {
    return invoiceHeader && invoiceHeader?.shipmentId == null && this.isShipmentOpen(this.currentShipment);
  }

  async addToShipment(invoiceHeader: InvoiceHeader) {
    await this.busyInvGrid(async () => {
      if (this.currentShipment == null) {
        this.currentShipment = this.uow.createEntity(Shipment, {
          modTs: new Date(),
        });
      }
      this.addToShipmentCore(invoiceHeader);
      await this.dbSaveService.saveChanges();
      this.hackInvGridRefreshRows();
  
    });
  }

  private addToShipmentCore(invoiceHeader: InvoiceHeader) {
    const shipment = this.currentShipment;
    // insure that any previous shipment info from shipstation is removed.
    shipment!.shipmentApiIdentifier = undefined;
    shipment!.freightAmt = undefined;
    shipment!.modTs = new Date(); // for OC purposes.
    shipment!.shipmentStatusId = ShipmentStatusEnum.New;
    invoiceHeader.shipment = <Shipment>shipment;

    if (shipment!.invoiceHeaders.length === 1) {
      invoiceHeader.isPrimaryInvoice = true;
    }
  }

  /** A shipment stops accepting invoice changes once it has a carrier label OR has been
   *  released for pickup. Checking labelApiIdentifier alone is not enough — a pickup shipment
   *  never gets one, so a null label there means "closed", not "not yet labeled". */
  private isShipmentOpen(shipment: Shipment | null | undefined) {
    return shipment == null || (shipment.labelApiIdentifier == null && !shipment.isPickup);
  }

  canRemoveFromShipment(invoiceHeader: InvoiceHeader) {
    return invoiceHeader?.shipment != null && this.isShipmentOpen(invoiceHeader.shipment);
  }

  async removeFromShipment(invoiceHeader: InvoiceHeader) {
    await this.busyInvGrid(async () => {
      const shipment = invoiceHeader.shipment;
      invoiceHeader.isPrimaryInvoice = false;
      invoiceHeader.shipment = undefined;
      // insure that any previous shipment info from shipEngine is removed.
      shipment.shipmentApiIdentifier = undefined;
      shipment.freightAmt = undefined;

      // cleanup empty shipping boxes and shipments
      if (shipment.invoiceHeaders.length === 0) {
        shipment.shippingBoxes.slice().forEach((sb) => {
          sb.entityAspect.setDeleted();
        });
        shipment.entityAspect.setDeleted();
        this.currentShipment = null;
      } else {
        shipment.modTs = new Date(); // for OC purposes
      }

      await this.dbSaveService.saveChanges();
      this.hackInvGridRefreshRows();
  
    });
  }

  setNewShipment() {
    this.currentShipment = null;

  }

  canCalcFreight(invoiceHeader: InvoiceHeader) {
    return invoiceHeader?.isPrimaryInvoice && invoiceHeader.invoiceStatusId == InvoiceStatusCode.Voucher && !invoiceHeader.isPickup;
  }

  async calcFreight(invoiceHeader: InvoiceHeader) {
    this.warnIfInvalidJoAddress(invoiceHeader.joHeader);

    // deliberately not using busyInvGrid here because we don't want to set busy flag when a dialog box
    // is going to be opened inside. We just want to disable the grid.
    await AgFns.busyGrid(this.invhGridOptions, null, async () => {
      const ok = await this.invoiceShipmentService.askAndCalcFreight(invoiceHeader);
      if (ok) {
        this.hackInvGridRefreshRows();
      }
    });
  }

  async printShipBinLabel(shipBin: ShipBin) {
    this.warnIfInvalidJoAddress(shipBin.firstJoh);
    return await this.barcodeService.printShipBinBarcode(shipBin);
  }

  async printInvoicePackageLabel(invh: InvoiceHeader) {
    this.warnIfInvalidJoAddress(invh.joHeader);
    return await this.barcodeService.printInvoicePackageBarcode(invh);
  }

  /** No-op. Shipping-address validation was disabled 2026-08-20 per the shipping team —
   *  toasts were noise and validation false-positives caused workflow interruptions. Call
   *  sites left intact so the flow structure is unchanged and re-enabling is a one-line revert. */
  async warnIfInvalidJoAddress(_jo: JoHeader) {
    return;
  }

  async printPackingSlip(invh: InvoiceHeader) {
    this.warnIfInvalidJoAddress(invh.joHeader);

    const r = await DialogBaseComponent.open(PackingSlipPrintDialogComponent, this, {
      invoice: invh,
      canPreview: true,
    });
    return <boolean>r;

    this.pdfurl =
      environment.apiRoot +
      "api/pdf/pdffromurl/packingSlip" +
      invh.id +
      "?url=" +
      UtilFns.getBaseUrl() +
      "printable-packing-slip/" +
      invh.id +
      "&headerLeft=Packing Slip " +
      invh.id;

    const pwin = window.open(this.pdfurl, "_blank");
    // if (this.poHeader.poStatusId === PoStatusCode.NewUnposted) {
    //   setTimeout(() => (this.isPrinting = true), 1000);
    // }
  }

  invoiceHasLabel(invh: InvoiceHeader) {
    // pickup shipments have no carrier label but still get a package barcode for the counter handoff
    return invh?.shipment?.labelApiIdentifier != null || invh?.isPickup === true;
  }

  canPrintShippingLabel(invh: InvoiceHeader) {
    return (
      (invh.invoiceStatusId === InvoiceStatusCode.Voucher || invh.invoiceStatusId === InvoiceStatusCode.VoucherLabeled) &&
      invh.isPrimaryInvoice &&
      invh.freightAmt != null &&
      !invh.isPickup
    );
  }

  async printShippingLabel(invh: InvoiceHeader) {
    await this.busyInvGrid(async () => {
      const ok = await this.invoiceShipmentService.printShippingLabel(invh);
      if (ok) {
        this.setNewShipment(); // current shipment is no longer available for assignment.
        await this.updateFromContext(); // re-query to remove labeled invoices from the list
      }
    });
  }

  canReleaseForPickup(invh: InvoiceHeader) {
    return (
      invh?.invoiceStatusId === InvoiceStatusCode.Voucher && invh.isPrimaryInvoice && invh.shipmentId != null && !invh.hasShippingLabel
    );
  }

  async releaseForPickup(invh: InvoiceHeader) {
    // Guard flips BEFORE the confirm await: a second click during the confirmation
    // window would otherwise run the release twice against the same shipment.
    if (this.isPickupActionBusy) {
      return;
    }
    this.isPickupActionBusy = true;
    try {
      const account = invh.joHeader?.account;
      const preamble = account?.isPickupAccount
        ? `Release invoice ${invh.id} for customer pickup?`
        : `${account?.accountName ?? "This account"} is not set up as a pickup account. ` +
          `Release invoice ${invh.id} for customer pickup anyway?`;
      const ok = await this.dialogService.yesNoDialog(
        "Release for Pickup",
        `${preamble}<br><br>No freight will be charged and no shipping label will be created. ` +
          `The invoice moves to End of Day for delivery and posting.`
      );
      if (!ok) {
        return;
      }

      await this.busyInvGrid(async () => {
        const released = await this.invoiceShipmentService.releaseForPickup(invh);
        if (released) {
          this.setNewShipment(); // current shipment is no longer available for assignment.
          await this.updateFromContext(); // re-query to drop released invoices from the list
          this.toastr.success("Released for pickup.");
        }
      });
    } finally {
      this.isPickupActionBusy = false;
    }
  }

  canUndoPickupRelease(invh: InvoiceHeader) {
    return invh?.isPickup && invh.isPrimaryInvoice && invh.invoiceStatusId === InvoiceStatusCode.VoucherLabeled;
  }

  async undoPickupRelease(invh: InvoiceHeader) {
    if (this.isPickupActionBusy) {
      return;
    }
    this.isPickupActionBusy = true;
    try {
      const ok = await this.dialogService.yesNoDialog(
        "Undo Pickup Release",
        `Return invoice ${invh.id} to In-Process Shipping?<br><br>It will drop off End of Day and can then be shipped by carrier instead.`
      );
      if (!ok) {
        return;
      }

      await this.busyInvGrid(async () => {
        const undone = await this.invoiceShipmentService.undoPickupRelease(invh);
        if (undone) {
          await this.updateFromContext();
          this.toastr.success("Pickup release undone. Invoice returned to In-Process Shipping.");
        }
      });
    } finally {
      this.isPickupActionBusy = false;
    }
  }

  voidAction(invh: InvoiceHeader) {
    if (this.canVoidShippingLabel(invh)) {
      this.voidShippingLabel(invh);
    } else if (this.canRemoveVoidError(invh)) {
      this.removeVoidError(invh);
    }
  }

  canVoidAction(invh: InvoiceHeader) {
    return this.canVoidShippingLabel(invh) || this.canRemoveVoidError(invh);
  }

  calcVoidActionLabel(invh: InvoiceHeader) {
    if (this.canVoidShippingLabel(invh)) {
      return "Void Label";
    } else if (this.canRemoveVoidError(invh)) {
      return "Remove Void Error";
    }
  }

  canVoidShippingLabel(invoiceHeader: InvoiceHeader) {
    // Pickup shipments reach VoucherLabeled without a carrier label. Voiding one would call
    // ShipEngine with a null labelApiIdentifier, fail, and strand every invoice on the
    // shipment in VOIDERR. They use Undo Pickup Release instead.
    return (
      invoiceHeader?.isPrimaryInvoice && invoiceHeader?.invoiceStatusId === InvoiceStatusCode.VoucherLabeled && !invoiceHeader.isPickup
    );
  }

  async voidShippingLabel(invoiceHeader: InvoiceHeader) {
    await this.busyInvGrid(async () => {
      const ok = await this.invoiceShipmentService.voidShippingLabel(invoiceHeader);
      // need to redraw even if it fails because a void error will be set
      this.hackInvGridRefreshRows();
    });
  }

  canRemoveVoidError(invoiceHeader: InvoiceHeader) {
    return invoiceHeader?.isPrimaryInvoice && invoiceHeader?.invoiceStatusId === InvoiceStatusCode.VoidError;
  }

  async removeVoidError(invoiceHeader: InvoiceHeader) {
    await this.busyInvGrid(async () => {
      const ok = await this.invoiceShipmentService.removeVoidError(invoiceHeader);
      if (ok) {
        this.hackInvGridRefreshRows();
      }
    });
  }


  hackInvGridRefreshRows() {
    this.invoiceHeaders = this.invoiceHeaders.slice();
    this.invhGridOptions.apiX?.refreshCells();
  }

  async busyInvGrid<T>(op: () => Promise<T>) {
    return await AgFns.busyGrid(this.invhGridOptions, this, op);
  }

  async openInvoiceDialog(invh: InvoiceHeader) {
    await InvoiceDialogComponent.openDialog(this.domainService.pngDialogService, {
      invoiceId: invh.id,
    });
  }

  async openFreightExplanation(invh: InvoiceHeader) {
    await DialogBaseComponent.open(FreightExplanationDialogComponent, this, {
      invoiceHeader: invh,
    });
  }
}
